"use client";

import { format } from "date-fns";
import { Card, CardContent } from "../card.js";
import { Button } from "../button.js";
import { Clock, Calendar, Utensils } from "lucide-react";

interface TimeSlot {
  start: string;
  end: string;
  isLunchSlot?: boolean;
}

interface TimeSlotCardProps {
  slot: TimeSlot;
  isSelected: boolean;
  onClick: (slot: TimeSlot) => void;
  date: string;
}

export const TimeSlotCard = ({ slot, isSelected, onClick, date }: TimeSlotCardProps) => {
  const startTime = new Date(slot.start);
  const endTime = new Date(slot.end);
  const isLunchSlot = slot.isLunchSlot || false;
  
  const formatTime = (date: Date) => {
    return format(date, "h:mm a");
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString + "T00:00:00");
    
return format(date, "EEEE, MMMM d");
  };

  // Different styling for lunch slots
  const getCardStyles = () => {
    if (isLunchSlot) {
      return isSelected 
        ? "ring-2 ring-orange-500 bg-orange-50 border-orange-200" 
        : "hover:border-orange-300 border-orange-200 bg-orange-25";
    }
    return isSelected 
      ? "ring-2 ring-blue-500 bg-blue-50 border-blue-200" 
      : "hover:border-blue-300 border-gray-200";
  };

  const getIconStyles = () => {
    if (isLunchSlot) {
      return isSelected ? "bg-orange-100" : "bg-orange-50";
    }
    return isSelected ? "bg-blue-100" : "bg-card-100";
  };

  const getIconColor = () => {
    if (isLunchSlot) {
      return isSelected ? "text-orange-600" : "text-orange-600";
    }
    return isSelected ? "text-blue-600" : "text-gray-600";
  };

  const getRadioStyles = () => {
    if (isLunchSlot) {
      return isSelected 
        ? "bg-orange-500 border-orange-500" 
        : "border-orange-300";
    }
    return isSelected 
      ? "bg-blue-500 border-blue-500" 
      : "border-gray-300";
  };

  return (
    <Card 
      className={`cursor-pointer transition-all duration-200 hover:shadow-md ${getCardStyles()}`}
      onClick={() => onClick(slot)}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-full ${getIconStyles()}`}>
              {isLunchSlot ? (
                <Utensils className={`h-4 w-4 ${getIconColor()}`} />
              ) : (
                <Clock className={`h-4 w-4 ${getIconColor()}`} />
              )}
            </div>
            <div>
              <div className="font-medium text-card-foreground flex items-center gap-2">
                {formatTime(startTime)} - {formatTime(endTime)}
                {isLunchSlot && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    Lunch
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-500 flex items-center">
                <Calendar className="h-3 w-3 mr-1" />
                {formatDate(date)}
              </div>
            </div>
          </div>
          <div className={`w-4 h-4 rounded-full border-2 ${getRadioStyles()}`}>
            {isSelected && (
              <div className="w-full h-full rounded-full bg-card scale-50"></div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
